/** @jsxImportSource @emotion/react */

import type { Interpolation } from "@emotion/react";
import type { ThemeType } from "@packages/ui";
import { Text } from "@packages/ui";
import * as RadixUIOneTimePasswordField from "@radix-ui/react-one-time-password-field";
import type { ComponentProps } from "react";

import { PIN_LENGTH } from "../../lib/constants/pinLength";
import { Screens } from "../../lib/enums/screens";

const cssDivContainer: Interpolation<ThemeType> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.ms,
  width: "100%",
});
const cssDivContainerCentered: Interpolation = {
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
};
const cssOneTimePasswordFieldInput: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  backgroundColor: "transparent",
  borderRadius: 10,
  borderColor: theme.semanticColors.neutralsVeryLow,
  borderStyle: "solid",
  borderWidth: theme.stroke.regular,
  color: theme.semanticColors.primary,
  display: "flex",
  height: theme.spacing.xxxl,
  justifyContent: "center",
  textAlign: "center",
  width: `min(100%, ${theme.spacing.xxl})`,
  [`@media ${Screens.Large}`]: {
    height: 80,
    width: `min(100%, ${theme.spacing.xxxl})`,
  },
});
const cssOneTimePasswordFieldInputError: Interpolation<ThemeType> = (
  theme,
) => ({
  borderColor: theme.semanticColors.errorMedium,
});
const cssOneTimePasswordFieldRoot: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: 10,
  width: "fit-content",
};
const cssOneTimePasswordFieldRootCentered: Interpolation = {
  justifyContent: "center",
};
const cssText: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.errorMedium,
});
const cssTextCentered: Interpolation = {
  textAlign: "center",
};

export const PinInput = ({
  error,
  isCentered,
  onValueChange,
  ...props
}: {
  error?: string;
  isCentered?: boolean;
  onValueChange: (value: string) => void;
} & Partial<ComponentProps<typeof RadixUIOneTimePasswordField.Root>>) => (
  <div css={[cssDivContainer, isCentered && cssDivContainerCentered]}>
    <RadixUIOneTimePasswordField.Root
      {...props}
      css={[
        cssOneTimePasswordFieldRoot,
        isCentered && cssOneTimePasswordFieldRootCentered,
      ]}
      id="pin"
      /* Use onValueChange instead of onChange to sync context, form, and Radix UI **/
      onChange={null}
      onValueChange={onValueChange}
    >
      {Array.from({ length: PIN_LENGTH }).map((_, index) => (
        <Text asChild key={index} size="m" type="headline">
          <RadixUIOneTimePasswordField.Input
            css={[
              cssOneTimePasswordFieldInput,
              error && cssOneTimePasswordFieldInputError,
            ]}
            id={`pin-${index}`}
          />
        </Text>
      ))}
      <RadixUIOneTimePasswordField.HiddenInput />
    </RadixUIOneTimePasswordField.Root>
    {error && (
      <Text
        css={[cssText, isCentered && cssTextCentered]}
        size="xl"
        type="detail"
      >
        {error}
      </Text>
    )}
  </div>
);
